import { zoneListQuerySchema, type PlaceMatch } from '@chukua/common';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { useEffect, useState } from 'react';
import { listZones, searchPlaces, type ZoneListItem } from '../zones.api';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const EXISTING_ZONE_LIMIT = 4;

/** `places` is null when place search is unavailable (no key, or MapTiler down): existing zones still show. */
export type PlaceSearchState =
  { status: 'idle' } | { status: 'searching' } | { status: 'found'; zones: ZoneListItem[]; places: PlaceMatch[] | null } | { status: 'failed' };

interface FoundState {
  term: string;
  state: PlaceSearchState;
}

interface PlaceSearchOptions {
  initialText?: string;
  /** Off for the map's search box, which suggests existing zones only. */
  includePlaces?: boolean;
  /** Off for the company's location, which is a place, never one of the zones. */
  includeZones?: boolean;
  /** Suggest zones before anything is typed (the map's box lists some on focus), and from the first letter. */
  suggestWhenEmpty?: boolean;
  zoneLimit?: number;
}

interface SearchRequest {
  term: string;
  includePlaces: boolean;
  /** 0 leaves existing zones out. */
  zoneLimit: number;
}

function findPlaces(term: string, map: MapLibreMap | null): Promise<PlaceMatch[] | null> {
  const center = map?.getCenter();
  return searchPlaces(term, center ? { lng: center.lng, lat: center.lat } : undefined).catch(() => null);
}

async function search({ term, includePlaces, zoneLimit }: SearchRequest, map: MapLibreMap | null): Promise<PlaceSearchState> {
  const [zones, places] = await Promise.all([
    zoneLimit ? listZones(zoneListQuerySchema.parse({ q: term || undefined, pageSize: zoneLimit })).then((page) => page.items) : Promise.resolve([]),
    includePlaces && term ? findPlaces(term, map) : Promise.resolve([]),
  ]);
  return { status: 'found', zones, places };
}

/** Idle until there is enough to search; searching until the results for what is typed now have arrived. */
function stateFor(term: string, minLength: number, found: FoundState | null): PlaceSearchState {
  if (term.length < minLength) return { status: 'idle' };
  return found?.term === term ? found.state : { status: 'searching' };
}

/**
 * Finding what a new zone is by name, as the admin types: the deployment's existing zones first (so a duplicate
 * is one click from being opened instead), then places from the geocoder, nearest the map's centre first.
 */
export function usePlaceSearch(
  map: MapLibreMap | null,
  { initialText = '', includePlaces = true, includeZones = true, suggestWhenEmpty = false, zoneLimit = EXISTING_ZONE_LIMIT }: PlaceSearchOptions = {},
) {
  const [text, setText] = useState(initialText);
  const [found, setFound] = useState<FoundState | null>(null);
  const term = text.trim();
  const minLength = suggestWhenEmpty ? 0 : MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (term.length < minLength) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      search({ term, includePlaces, zoneLimit: includeZones ? zoneLimit : 0 }, map)
        .catch((): PlaceSearchState => ({ status: 'failed' }))
        .then((state) => {
          if (!cancelled) setFound({ term, state });
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term, map, includePlaces, includeZones, zoneLimit, minLength]);

  return { text, setText, state: stateFor(term, minLength, found) };
}
